import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  InputAdornment,
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { useMemo, useState } from "react";
import { API_URL } from "../api/auth";
import { useClerkToken } from "../hooks/use-clerk-token";
import { useSafeNavigateBack } from "../hooks/use-safe-navigate-back";

interface Repo {
  owner: string;
  repo: string;
  full_name: string;
  private: boolean;
}

export function IntegrationsGitHubSetupPage() {
  const navigateBack = useSafeNavigateBack("/configuration?tab=integrations");
  const { token } = useClerkToken();
  const { enqueueSnackbar } = useSnackbar();

  const {
    data: setupData,
    isLoading,
    error: queryError,
  } = useQuery({
    queryKey: ["github-setup", token],
    queryFn: async () => {
      const [reposResponse, configResponse] = await Promise.all([
        fetch(`${API_URL}/oauth/github/repos`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API_URL}/oauth/github/config`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      if (!reposResponse.ok) {
        const errorData = await reposResponse.json();
        throw new Error(errorData.detail || "Failed to fetch repositories");
      }

      const repos: Repo[] = await reposResponse.json();
      let initialSelected: string[] = [];

      if (configResponse.ok) {
        const configData = await configResponse.json();
        if (configData.configured && configData.selected_repos) {
          initialSelected = configData.selected_repos.map(
            (r: { full_name: string }) => r.full_name,
          );
        }
      }

      return { repos, initialSelected };
    },
    enabled: !!token,
    refetchOnWindowFocus: false,
    refetchInterval: false,
  });

  const repos = setupData?.repos ?? [];
  const error = queryError ? queryError.message : null;

  const [selected, setSelected] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize selected from config when data first loads
  const [prevSetupData, setPrevSetupData] = useState(setupData);
  if (setupData && setupData !== prevSetupData) {
    setPrevSetupData(setupData);
    if (setupData.initialSelected.length > 0) {
      setSelected(setupData.initialSelected);
    }
  }

  // Filter state
  const [searchQuery, setSearchQuery] = useState("");
  const [ownerFilter, setOwnerFilter] = useState<string>("all");
  const [visibilityFilter, setVisibilityFilter] = useState<string>("all");

  // Get unique owners for filter dropdown
  const owners = useMemo(() => {
    const uniqueOwners = [...new Set(repos.map((r) => r.owner))];
    return uniqueOwners.sort();
  }, [repos]);

  // Filter repos based on search, owner, and visibility
  const filteredRepos = useMemo(() => {
    return repos.filter((repo) => {
      const matchesSearch =
        searchQuery === "" ||
        repo.full_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        repo.repo.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesOwner = ownerFilter === "all" || repo.owner === ownerFilter;

      const matchesVisibility =
        visibilityFilter === "all" ||
        (visibilityFilter === "private" && repo.private) ||
        (visibilityFilter === "public" && !repo.private);

      return matchesSearch && matchesOwner && matchesVisibility;
    });
  }, [repos, searchQuery, ownerFilter, visibilityFilter]);

  const handleToggle = (fullName: string) => {
    setSelected((prev) =>
      prev.includes(fullName) ? prev.filter((r) => r !== fullName) : [...prev, fullName],
    );
  };

  const handleSelectAllFiltered = () => {
    const filteredFullNames = filteredRepos.map((r) => r.full_name);
    const allFilteredSelected = filteredFullNames.every((fn) => selected.includes(fn));

    if (allFilteredSelected) {
      // Deselect all filtered repos
      setSelected((prev) => prev.filter((fn) => !filteredFullNames.includes(fn)));
    } else {
      // Select all filtered repos (add to existing selection)
      setSelected((prev) => [...new Set([...prev, ...filteredFullNames])]);
    }
  };

  const handleSubmit = async () => {
    if (!token) {
      enqueueSnackbar("Please log in to continue", { variant: "error" });
      return;
    }

    setIsSubmitting(true);

    try {
      const selectedRepos = repos.filter((r) => selected.includes(r.full_name));

      const response = await fetch(`${API_URL}/oauth/github/setup-repos`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          selected_repos: selectedRepos,
          default_repo: selectedRepos[0]?.full_name || null,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to save repositories");
      }

      enqueueSnackbar("GitHub repositories configured successfully!", {
        variant: "success",
      });

      setTimeout(() => {
        navigateBack();
      }, 1500);
    } catch (err) {
      console.error("Failed to setup GitHub repos:", err);
      enqueueSnackbar(err instanceof Error ? err.message : "Failed to save repositories", {
        variant: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkip = () => {
    navigateBack();
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          Loading repositories...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          p: 2,
          backgroundColor: "background.default",
        }}
      >
        <Card sx={{ maxWidth: 600, width: "100%" }}>
          <CardContent>
            <Stack spacing={3} alignItems="center">
              <Typography variant="h5" color="error">
                Failed to Load Repositories
              </Typography>
              <Typography color="text.secondary">{error}</Typography>
              <Button variant="contained" onClick={navigateBack}>
                Go Back
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    );
  }

  const filteredFullNames = filteredRepos.map((r) => r.full_name);
  const allFilteredSelected =
    filteredRepos.length > 0 && filteredFullNames.every((fn) => selected.includes(fn));

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        p: 2,
        backgroundColor: "background.default",
      }}
    >
      <Card sx={{ maxWidth: 700, width: "100%" }}>
        <CardContent>
          <Stack spacing={3}>
            <Box>
              <Typography variant="h5" gutterBottom>
                Select GitHub Repositories
              </Typography>
              <Typography variant="body2" color="text.secondary">
                GitHub connected successfully! Choose which repositories to connect with desplega.ai
                for PR creation and test linking.
              </Typography>
            </Box>

            {/* Search and Filter Controls */}
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                size="small"
                placeholder="Search repositories..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ flexGrow: 1 }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  },
                }}
              />
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Owner</InputLabel>
                <Select
                  value={ownerFilter}
                  label="Owner"
                  onChange={(e) => setOwnerFilter(e.target.value)}
                >
                  <MenuItem value="all">All owners</MenuItem>
                  {owners.map((owner) => (
                    <MenuItem key={owner} value={owner}>
                      {owner}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Visibility</InputLabel>
                <Select
                  value={visibilityFilter}
                  label="Visibility"
                  onChange={(e) => setVisibilityFilter(e.target.value)}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="public">Public</MenuItem>
                  <MenuItem value="private">Private</MenuItem>
                </Select>
              </FormControl>
            </Stack>

            {/* Selection Info and Actions */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {selected.length} selected
                {filteredRepos.length !== repos.length && (
                  <>
                    {" "}
                    · Showing {filteredRepos.length} of {repos.length} repos
                  </>
                )}
              </Typography>
              <Button size="small" onClick={handleSelectAllFiltered}>
                {allFilteredSelected ? "Deselect All" : "Select All"}
                {filteredRepos.length !== repos.length && " Filtered"}
              </Button>
            </Box>

            {/* Repository List */}
            <List
              sx={{
                maxHeight: 400,
                overflow: "auto",
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
              }}
            >
              {filteredRepos.map((repo) => (
                <ListItem key={repo.full_name} disablePadding>
                  <ListItemButton
                    onClick={() => handleToggle(repo.full_name)}
                    disabled={isSubmitting}
                  >
                    <ListItemIcon>
                      <Checkbox
                        edge="start"
                        checked={selected.includes(repo.full_name)}
                        tabIndex={-1}
                        disableRipple
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={repo.full_name}
                      secondary={
                        <Chip
                          label={repo.private ? "Private" : "Public"}
                          size="small"
                          color={repo.private ? "default" : "success"}
                          sx={{ mt: 0.5 }}
                        />
                      }
                    />
                  </ListItemButton>
                </ListItem>
              ))}
              {filteredRepos.length === 0 && repos.length > 0 && (
                <ListItem>
                  <ListItemText
                    primary="No matching repositories"
                    secondary="Try adjusting your search or filter"
                  />
                </ListItem>
              )}
              {repos.length === 0 && (
                <ListItem>
                  <ListItemText
                    primary="No repositories found"
                    secondary="You don't have access to any repositories"
                  />
                </ListItem>
              )}
            </List>

            <Typography variant="body2" color="text.secondary">
              You can modify your repository selection later in the Configuration settings.
            </Typography>

            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button variant="outlined" onClick={handleSkip} disabled={isSubmitting}>
                Skip for now
              </Button>
              <Button variant="contained" onClick={handleSubmit} disabled={isSubmitting}>
                {isSubmitting ? <CircularProgress size={24} color="inherit" /> : "Complete Setup"}
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
